import React, { useState, useEffect, useCallback } from 'react';

const API_URL = 'https://skortes.com';
const LINK_DAFTAR = 'https://skortes.com'; // link registrasi user, kode ditempel sebagai ?kode=

// ─── Helper API ───────────────────────────────────────────────
async function apiCall(endpoint, method = 'GET', body = null, token = null) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : null,
  });
  return res.json();
}

function formatTgl(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getDate()} ${['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Agu','Sep','Okt','Nov','Des'][d.getMonth()]} ${d.getFullYear()}`;
}

const MODUL_LABEL = {
  kraepelin: { nama: 'Kraepelin / Pauli', icon: '🧮' },
  kecermatan: { nama: 'Tes Kecermatan', icon: '✅' },
  logika: { nama: 'Logika & Numerik', icon: '🔢' },
  verbal: { nama: 'Verbal', icon: '📖' },
  figural: { nama: 'Figural & Spasial', icon: '🧩' },
  kepribadian: { nama: 'Kepribadian', icon: '🧠' },
  akademik: { nama: 'Kemampuan Akademik', icon: '📚' },
  armyalpha: { nama: 'Instruksi Cepat', icon: '⚡' },
};

// ─── Grafik garis kecil per modul (sama seperti punya Admin Panel) ──
function GrafikModulPenuh({ modulId, data }) {
  const W = 320, H = 90, PAD = 20;
  const skorList = data.map(d => d.skor);
  const maks = Math.max(100, ...skorList);
  const points = data.map((d, i) => {
    const x = PAD + (i / Math.max(1, data.length - 1)) * (W - PAD * 2);
    const y = H - PAD - (d.skor / maks) * (H - PAD * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  const info = MODUL_LABEL[modulId] || { nama: modulId, icon: '📝' };
  const terakhir = data[data.length - 1]?.skor;
  const rataModul = Math.round(skorList.reduce((a, b) => a + b, 0) / skorList.length);

  return (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{info.icon} {info.nama}</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>rata² <span className="font-bold font-mono" style={{ color: 'var(--accent)' }}>{rataModul}</span></p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ height: 70 }}>
        <line x1={PAD} y1={H-PAD} x2={W-PAD} y2={H-PAD} stroke="var(--border)" strokeWidth="1" />
        <polyline points={points} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {data.map((d, i) => {
          const x = PAD + (i / Math.max(1, data.length - 1)) * (W - PAD * 2);
          const y = H - PAD - (d.skor / maks) * (H - PAD * 2);
          return <circle key={i} cx={x.toFixed(1)} cy={y.toFixed(1)} r="2.5" fill="var(--accent)" />;
        })}
      </svg>
      <div className="flex justify-between text-[10px]" style={{ color: 'var(--text-muted)' }}>
        <span>{formatTgl(data[0]?.tanggal)}</span>
        <span>{data.length} sesi</span>
        <span>{formatTgl(data[data.length - 1]?.tanggal)}</span>
      </div>
      {terakhir != null && (
        <p className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>Skor terakhir: <span className="font-bold" style={{ color: 'var(--text-primary)' }}>{terakhir}</span></p>
      )}
    </div>
  );
}

// ─── Layar Login Sub-Admin ──────────────────────────────────────
function LoginSubAdmin({ onLogin }) {
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const handleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiCall('/api/login', 'POST', { email, password });
      if (data.token && (data.user.role === 'subadmin' || data.user.role === 'admin')) {
        localStorage.setItem('subadminToken', data.token);
        localStorage.setItem('subadminUser', JSON.stringify(data.user));
        onLogin(data.token, data.user);
      } else if (data.token) {
        setError('Akun ini bukan akun sub-admin.');
      } else {
        setError(data.error || 'Login gagal');
      }
    } catch {
      setError('Tidak bisa terhubung ke server');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--bg-app)' }}>
      <div className="w-full max-w-sm p-8 rounded-2xl" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'var(--accent)' }}>
            <span className="text-2xl">🔗</span>
          </div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
            PANEL SUB-ADMIN
          </h1>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Skortes — Referral</p>
        </div>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: 'var(--danger)', border: '1px solid var(--danger)' }}>
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Email</p>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="email@contoh.com"
              className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            />
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider mb-1.5" style={{ color: 'var(--text-muted)' }}>Password</p>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
              className="w-full px-4 py-3 rounded-xl text-sm focus:outline-none"
              style={{ backgroundColor: 'var(--bg-card2)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
            />
          </div>
          <button onClick={handleLogin} disabled={loading}
            className="w-full py-3.5 rounded-xl font-bold text-sm uppercase tracking-wide mt-2"
            style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)', fontFamily: "'Poppins', sans-serif", opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Masuk...' : 'Masuk →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Kartu Statistik kecil ───────────────────────────────────────
function KartuStat({ icon, label, value, color }) {
  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-2 mb-1.5">
        <span className="text-lg">{icon}</span>
        <p className="text-[11px] uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
      <p className="text-2xl font-extrabold font-mono" style={{ color }}>{value}</p>
    </div>
  );
}

// ─── Bagian Kode Referral ────────────────────────────────────────
function KodeReferral({ token, onBerubah }) {
  const [kodes, setKodes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [membuat, setMembuat] = useState(false);
  const [keterangan, setKeterangan] = useState('');
  const [disalin, setDisalin] = useState(null);

  const load = useCallback(() => {
    apiCall('/api/subadmin/kode', 'GET', null, token)
      .then(d => { setKodes(d.kodes || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const handleBuat = async () => {
    setMembuat(true);
    const data = await apiCall('/api/subadmin/kode', 'POST', { keterangan }, token);
    setMembuat(false);
    if (data.kode) {
      setKeterangan('');
      load();
      onBerubah && onBerubah();
    }
  };

  const handleToggle = async (id, aktifSekarang) => {
    await apiCall(`/api/subadmin/kode/${id}`, 'PUT', { aktif: !aktifSekarang }, token);
    load();
  };

  const salinLink = (kode) => {
    const link = `${LINK_DAFTAR}/?kode=${kode}`;
    navigator.clipboard?.writeText(link);
    setDisalin(kode);
    setTimeout(() => setDisalin(null), 1500);
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
        KODE REFERRAL SAYA
      </h2>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Bagikan link di bawah ke calon user. Siapa pun yang daftar lewat link itu otomatis tercatat sebagai referral kamu — bisa kamu pantau di tab "User Referral".
      </p>

      <div className="rounded-xl p-4 space-y-3" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--accent)' }}>
        <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>+ Buat Kode Baru</p>
        <input type="text" placeholder="Label (opsional, mis. 'Grup A')" value={keterangan}
          onChange={e => setKeterangan(e.target.value)}
          className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none"
          style={{ backgroundColor: 'var(--bg-card)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
        />
        <button onClick={handleBuat} disabled={membuat}
          className="w-full py-2.5 rounded-xl font-bold text-sm"
          style={{ backgroundColor: 'var(--accent)', color: 'var(--bg-app)' }}>
          {membuat ? 'Membuat...' : 'Buat Kode Referral'}
        </button>
      </div>

      {loading ? (
        <p className="text-center py-6 text-sm" style={{ color: 'var(--text-muted)' }}>Memuat...</p>
      ) : kodes.length === 0 ? (
        <div className="text-center py-8 rounded-xl" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-3xl mb-2">🔗</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Belum punya kode referral. Buat yang pertama di atas!</p>
        </div>
      ) : (
        <div className="space-y-2">
          {kodes.map(k => (
            <div key={k.id} className="rounded-xl p-4" style={{ backgroundColor: 'var(--bg-card2)', border: `1px solid ${k.aktif ? 'var(--accent)' : 'var(--border)'}` }}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="font-bold font-mono text-sm" style={{ color: 'var(--accent)' }}>{k.kode}</p>
                  {k.keterangan && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{k.keterangan}</p>}
                </div>
                <span className="text-[10px] px-2 py-0.5 rounded-full"
                      style={{ backgroundColor: 'var(--bg-card3)', color: k.aktif ? 'var(--success)' : 'var(--text-muted)', border: '1px solid var(--border)' }}>
                  {k.aktif ? '● Aktif' : '○ Nonaktif'}
                </span>
              </div>
              <p className="text-[11px] mb-2 break-all" style={{ color: 'var(--text-faint)' }}>{LINK_DAFTAR}/?kode={k.kode}</p>
              <p className="text-[11px] mb-3" style={{ color: 'var(--text-muted)' }}>Terpakai {k.terpakai || 0}x · Dibuat {formatTgl(k.created_at)}</p>
              <div className="flex gap-2">
                <button onClick={() => salinLink(k.kode)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)', border: '1px solid var(--border)' }}>
                  {disalin === k.kode ? '✓ Tersalin' : '📋 Salin Link'}
                </button>
                <button onClick={() => handleToggle(k.id, k.aktif)}
                  className="flex-1 py-2 rounded-lg text-xs font-semibold"
                  style={{ backgroundColor: k.aktif ? 'rgba(220,38,38,0.1)' : 'var(--bg-card3)', color: k.aktif ? 'var(--danger)' : 'var(--success)', border: '1px solid var(--border)' }}>
                  {k.aktif ? 'Nonaktifkan' : 'Aktifkan'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Bagian Daftar User Referral ─────────────────────────────────
function UserReferral({ token }) {
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [dibuka, setDibuka]   = useState(null);
  const [pembayaran, setPembayaran] = useState({}); // cache per userId
  const [perkembangan, setPerkembangan] = useState({}); // cache per userId

  useEffect(() => {
    apiCall('/api/subadmin/users', 'GET', null, token)
      .then(d => { setUsers(d.users || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [token]);

  const bukaDetail = async (id) => {
    if (dibuka === id) { setDibuka(null); return; }
    setDibuka(id);
    if (!pembayaran[id]) {
      const data = await apiCall(`/api/subadmin/users/${id}/pembayaran`, 'GET', null, token);
      setPembayaran(prev => ({ ...prev, [id]: data.pembayaran || [] }));
    }
    if (!perkembangan[id]) {
      const data = await apiCall(`/api/subadmin/users/${id}/perkembangan`, 'GET', null, token);
      setPerkembangan(prev => ({ ...prev, [id]: data }));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
          USER REFERRAL
        </h2>
        <span className="text-sm px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)' }}>
          {users.length} orang
        </span>
      </div>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Daftar user yang mendaftar pakai kode referral kamu. Semua pembayaran tetap masuk ke sistem Skortes langsung — ini cuma untuk kamu pantau, bukan menerima dana.
      </p>

      {loading ? (
        <p className="text-center py-8 text-sm" style={{ color: 'var(--text-muted)' }}>Memuat...</p>
      ) : users.length === 0 ? (
        <div className="text-center py-12 rounded-xl" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
          <p className="text-4xl mb-3">👥</p>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Belum ada user yang daftar lewat kode kamu.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {users.map(u => (
            <div key={u.id}>
              <div onClick={() => bukaDetail(u.id)}
                className="rounded-xl p-4 cursor-pointer"
                style={{ backgroundColor: dibuka === u.id ? 'var(--bg-card3)' : 'var(--bg-card2)', border: `1px solid ${dibuka === u.id ? 'var(--accent)' : 'var(--border)'}` }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm"
                         style={{ backgroundColor: 'var(--bg-card3)', color: 'var(--accent)' }}>
                      {u.nama.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>{u.nama}</p>
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{u.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-xs font-mono font-bold" style={{ color: u.jumlah_bayar_sukses > 0 ? 'var(--success)' : 'var(--text-faint)' }}>
                      {u.jumlah_bayar_sukses > 0 ? `✓ ${u.jumlah_bayar_sukses}x bayar` : 'Belum bayar'}
                    </p>
                    <p className="text-[11px]" style={{ color: 'var(--text-faint)' }}>{formatTgl(u.tanggal_daftar)}</p>
                  </div>
                </div>
              </div>
              {dibuka === u.id && (
                <div className="rounded-xl p-4 -mt-1 space-y-3" style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--accent)', borderTop: 'none', borderRadius: '0 0 12px 12px' }}>
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <p style={{ color: 'var(--text-faint)' }}>Skor rata-rata</p>
                      <p className="font-bold font-mono" style={{ color: 'var(--accent)' }}>{u.skor_rata_rata != null ? u.skor_rata_rata : '—'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-faint)' }}>Terakhir aktif</p>
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{u.terakhir_aktif ? formatTgl(u.terakhir_aktif) : 'Belum pernah'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-faint)' }}>Jalur</p>
                      <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{u.jalur || '—'}</p>
                    </div>
                    <div>
                      <p style={{ color: 'var(--text-faint)' }}>Kode dipakai</p>
                      <p className="font-mono font-semibold" style={{ color: 'var(--text-primary)' }}>{u.kode_dipakai}</p>
                    </div>
                  </div>

                  {!perkembangan[u.id] ? (
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Memuat progres latihan...</p>
                  ) : (
                    <>
                      <div className="grid grid-cols-3 gap-2 text-xs">
                        <div className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--bg-card2)' }}>
                          <p style={{ color: 'var(--text-faint)' }}>Total Sesi</p>
                          <p className="font-bold font-mono" style={{ color: 'var(--text-primary)' }}>{perkembangan[u.id].ringkasan?.totalSesi ?? 0}</p>
                        </div>
                        <div className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--bg-card2)' }}>
                          <p style={{ color: 'var(--text-faint)' }}>Tren 14 Hari</p>
                          <p className="font-bold font-mono" style={{ color: perkembangan[u.id].ringkasan?.tren14Hari > 0 ? 'var(--success)' : perkembangan[u.id].ringkasan?.tren14Hari < 0 ? 'var(--danger)' : 'var(--text-primary)' }}>
                            {perkembangan[u.id].ringkasan?.tren14Hari != null ? (perkembangan[u.id].ringkasan.tren14Hari > 0 ? `+${perkembangan[u.id].ringkasan.tren14Hari}` : perkembangan[u.id].ringkasan.tren14Hari) : '—'}
                          </p>
                        </div>
                        <div className="rounded-lg p-2 text-center" style={{ backgroundColor: 'var(--bg-card2)' }}>
                          <p style={{ color: 'var(--text-faint)' }}>Streak</p>
                          <p className="font-bold font-mono" style={{ color: 'var(--accent)' }}>{perkembangan[u.id].ringkasan?.streak ?? 0}🔥</p>
                        </div>
                      </div>

                      {perkembangan[u.id].peringatan?.length > 0 && (
                        <div className="rounded-lg p-3 space-y-1" style={{ backgroundColor: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)' }}>
                          <p className="text-[11px] font-bold uppercase tracking-wider" style={{ color: '#FBBF24' }}>⚠️ Perlu Perhatian</p>
                          {perkembangan[u.id].peringatan.map((p, i) => (
                            <p key={i} className="text-xs" style={{ color: 'var(--text-muted)' }}>• {p}</p>
                          ))}
                        </div>
                      )}

                      {perkembangan[u.id].perModul && Object.keys(perkembangan[u.id].perModul).length > 0 && (
                        <div className="space-y-2">
                          <p className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--accent)' }}>Progres per Modul</p>
                          {Object.entries(perkembangan[u.id].perModul).map(([modulId, data]) => (
                            <GrafikModulPenuh key={modulId} modulId={modulId} data={data} />
                          ))}
                        </div>
                      )}
                    </>
                  )}

                  <div>
                    <p className="text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: 'var(--accent)' }}>Riwayat Pembayaran</p>
                    {!pembayaran[u.id] ? (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Memuat...</p>
                    ) : pembayaran[u.id].length === 0 ? (
                      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Belum pernah bayar.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {pembayaran[u.id].map(p => (
                          <div key={p.id} className="flex items-center justify-between text-xs rounded-lg px-3 py-2" style={{ backgroundColor: 'var(--bg-card2)' }}>
                            <div>
                              <p style={{ color: 'var(--text-primary)' }}>{p.tryout_judul || `Tryout #${p.id}`}</p>
                              <p style={{ color: 'var(--text-faint)' }}>{p.provider || 'midtrans'} · {formatTgl(p.created_at)}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-bold" style={{ color: 'var(--text-primary)' }}>Rp{Number(p.jumlah).toLocaleString('id-ID')}</p>
                              <p style={{ color: p.status === 'success' ? 'var(--success)' : p.status === 'failed' ? 'var(--danger)' : 'var(--text-muted)' }}>{p.status}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Panel Sub-Admin (utama) ──────────────────────────────────────
export default function SubAdminPanel() {
  const [token, setToken] = useState(() => localStorage.getItem('subadminToken'));
  const [user, setUser]   = useState(() => {
    try { return JSON.parse(localStorage.getItem('subadminUser')); } catch { return null; }
  });
  const [menu, setMenu]   = useState('ringkasan');
  const [ringkasan, setRingkasan] = useState({});

  const muatRingkasan = useCallback(() => {
    if (!token) return;
    apiCall('/api/subadmin/ringkasan', 'GET', null, token)
      .then(d => setRingkasan(d))
      .catch(() => {});
  }, [token]);

  useEffect(() => { muatRingkasan(); }, [muatRingkasan]);

  const handleLogin = (t, u) => { setToken(t); setUser(u); };
  const handleLogout = () => {
    localStorage.removeItem('subadminToken');
    localStorage.removeItem('subadminUser');
    setToken(null);
    setUser(null);
  };

  if (!token || !user) return <LoginSubAdmin onLogin={handleLogin} />;

  const MENU = [
    { id: 'ringkasan', label: 'Ringkasan', icon: '📊' },
    { id: 'kode', label: 'Kode Referral', icon: '🔗' },
    { id: 'users', label: 'User Referral', icon: '👥' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-app)' }}>

      <div className="px-6 py-4 flex items-center justify-between" style={{ backgroundColor: 'var(--bg-card)', borderBottom: '1px solid var(--border)' }}>
        <div className="flex items-center gap-3">
          <span className="text-xl">🔗</span>
          <div>
            <p className="font-bold text-sm" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
              PANEL SUB-ADMIN
            </p>
            <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Skortes — Referral</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <p className="text-xs" style={{ color: 'var(--accent)' }}>👤 {user.nama}</p>
          <button onClick={handleLogout} className="text-xs px-3 py-1.5 rounded-lg"
                  style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: 'var(--danger)', border: '1px solid var(--danger)' }}>
            Keluar
          </button>
        </div>
      </div>

      <div className="flex">
        <div className="w-48 min-h-screen p-4 space-y-1 flex-shrink-0" style={{ backgroundColor: 'var(--bg-card)', borderRight: '1px solid var(--border)' }}>
          {MENU.map(m => (
            <button key={m.id} onClick={() => setMenu(m.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-left transition"
              style={{
                backgroundColor: menu === m.id ? 'var(--bg-card3)' : 'transparent',
                color: menu === m.id ? 'var(--accent)' : 'var(--text-muted)',
                border: menu === m.id ? '1px solid var(--border)' : '1px solid transparent',
              }}>
              <span>{m.icon}</span> {m.label}
            </button>
          ))}
        </div>

        <div className="flex-1 p-6 max-w-3xl">
          {menu === 'ringkasan' && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: "'Poppins', sans-serif" }}>
                RINGKASAN
              </h2>
              <div className="grid grid-cols-3 gap-3">
                <KartuStat icon="👥" label="Total User" value={ringkasan.totalUser || 0} color="var(--accent)" />
                <KartuStat icon="✅" label="Sudah Bayar" value={ringkasan.totalSudahBayar || 0} color="var(--success)" />
                <KartuStat icon="🔗" label="Kode Referral" value={ringkasan.totalKode || 0} color="#60A5FA" />
              </div>
              <div className="rounded-xl p-5" style={{ backgroundColor: 'var(--bg-card2)', border: '1px solid var(--border)' }}>
                <p className="text-sm font-bold mb-2" style={{ color: 'var(--accent)', fontFamily: "'Poppins', sans-serif" }}>
                  ℹ️ Cara Kerja
                </p>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--text-muted)' }}>
                  Buat kode referral di tab "Kode Referral", bagikan link-nya ke calon user. Siapa pun yang daftar lewat link itu otomatis tercatat sebagai referral kamu, bisa dipantau di tab "User Referral" — termasuk progres latihan dan status pembayaran mereka. Semua transaksi pembayaran tetap masuk langsung ke sistem Skortes, akun ini cuma untuk memantau, bukan mengelola data admin lain.
                </p>
              </div>
            </div>
          )}
          {menu === 'kode' && <KodeReferral token={token} onBerubah={muatRingkasan} />}
          {menu === 'users' && <UserReferral token={token} />}
        </div>
      </div>
    </div>
  );
}
